import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { procedureService } from '../services/dataService';
import styles from './assets/css/ProceduresPage.module.css';

const initialCreateForm = {
  procedure_code: '',
  name: '',
  category: '',
  procedure_type: '',
  description: '',
  reimbursement_amount: '',
  currency: 'KES',
  effective_from: '',
  effective_to: '',
  lifecycle_state: 'active',
};

const initialEditForm = {
  name: '',
  category: '',
  procedure_type: '',
  description: '',
  reimbursement_amount: '',
  effective_to: '',
  lifecycle_state: 'active',
};

const ProceduresPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [lifecycleState, setLifecycleState] = useState('');

  const [createForm, setCreateForm] = useState(initialCreateForm);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editId, setEditId] = useState('');
  const [editForm, setEditForm] = useState(initialEditForm);
  const [saving, setSaving] = useState(false);

  const activeCount = useMemo(
    () => items.filter((item) => item.lifecycle_state === 'active').length,
    [items]
  );

  const buildCreatePayload = () => ({
    ...createForm,
    reimbursement_amount: Number(createForm.reimbursement_amount),
    effective_from: new Date(createForm.effective_from).toISOString(),
    effective_to: createForm.effective_to ? new Date(createForm.effective_to).toISOString() : null,
  });

  const buildEditPayload = () => ({
    ...editForm,
    reimbursement_amount: editForm.reimbursement_amount ? Number(editForm.reimbursement_amount) : undefined,
    effective_to: editForm.effective_to ? new Date(editForm.effective_to).toISOString() : undefined,
  });

  const loadProcedures = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await procedureService.getAll(0, 500);
      const rows = Array.isArray(data) ? data : [];

      const term = search.trim().toLowerCase();
      const filtered = rows.filter((row) => {
        const lifecycleOk = lifecycleState ? row.lifecycle_state === lifecycleState : true;
        if (!lifecycleOk) return false;
        if (!term) return true;

        return [
          row.procedure_code,
          row.name,
          row.category,
          row.procedure_type,
          row.lifecycle_state,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term));
      });

      setItems(filtered);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load procedures');
    } finally {
      setLoading(false);
    }
  }, [search, lifecycleState]);

  useEffect(() => {
    loadProcedures();
  }, [loadProcedures]);

  const onSearch = (e) => {
    e.preventDefault();
    loadProcedures();
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await procedureService.create(buildCreatePayload());
      setCreateForm(initialCreateForm);
      setShowCreateModal(false);
      await loadProcedures();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to create procedure');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (item) => {
    setEditId(item.id);
    setEditForm({
      name: item.name || '',
      category: item.category || '',
      procedure_type: item.procedure_type || '',
      description: item.description || '',
      reimbursement_amount: item.reimbursement_amount ?? '',
      effective_to: item.effective_to ? String(item.effective_to).slice(0, 10) : '',
      lifecycle_state: item.lifecycle_state || 'active',
    });
  };

  const cancelEdit = () => {
    setEditId('');
    setEditForm(initialEditForm);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editId) return;
    setSaving(true);
    setError('');
    try {
      await procedureService.update(editId, buildEditPayload());
      cancelEdit();
      await loadProcedures();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to update procedure');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <h1>Procedures</h1>
        <button type="button" className={styles.addButton} onClick={() => setShowCreateModal(true)}>
          Add Procedure
        </button>
      </div>

      <div className={styles.panel}>
        <h2>Procedure Metrics</h2>
        <div className={styles.metricsRow}>
          <span>Total: <strong>{items.length}</strong></span>
          <span>Active: <strong>{activeCount}</strong></span>
        </div>
      </div>

      <div className={styles.panel}>
        <h2>Filters</h2>
        <form onSubmit={onSearch} className={styles.grid4}>
          <input
            placeholder="Search by code, name, category"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select value={lifecycleState} onChange={(e) => setLifecycleState(e.target.value)}>
            <option value="">All lifecycle states</option>
            <option value="active">active</option>
            <option value="suspended">suspended</option>
            <option value="retired">retired</option>
          </select>
          <button type="submit">Apply</button>
        </form>
      </div>

      {!showCreateModal && !editId && error ? <div className={styles.error}>{error}</div> : null}

      <div className={styles.panel}>
        <h2>Procedure List</h2>
        {loading ? <p>Loading procedures...</p> : null}
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Category</th>
                <th>Type</th>
                <th>Reimbursement</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.procedure_code}</td>
                  <td>{item.name}</td>
                  <td>{item.category || '-'}</td>
                  <td>{item.procedure_type || '-'}</td>
                  <td> {item.currency}. {item.reimbursement_amount.toLocaleString()}</td>
                  <td>{item.lifecycle_state}</td>
                  <td className={styles.actions}>
                    <button type="button" onClick={() => startEdit(item)}>Edit</button>
                  </td>
                </tr>
              ))}
              {!loading && items.length === 0 ? (
                <tr>
                  <td colSpan={7}>No procedures found.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {showCreateModal ? (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>Create Procedure</h2>
              <button type="button" className={styles.secondary} onClick={() => setShowCreateModal(false)}>
                Close
              </button>
            </div>
            {error ? <div className={styles.error}>{error}</div> : null}
            <form onSubmit={handleCreate} className={styles.grid4}>
              <input
                required
                placeholder="Procedure code"
                value={createForm.procedure_code}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, procedure_code: e.target.value }))}
              />
              <input
                required
                placeholder="Procedure name"
                value={createForm.name}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, name: e.target.value }))}
              />
              <input
                placeholder="Category"
                value={createForm.category}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, category: e.target.value }))}
              />
              <input
                placeholder="Procedure type"
                value={createForm.procedure_type}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, procedure_type: e.target.value }))}
              />

              <input
                required
                type="number"
                min={0}
                step="0.01"
                placeholder="Reimbursement amount"
                value={createForm.reimbursement_amount}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, reimbursement_amount: e.target.value }))}
              />
              <input
                maxLength={3}
                placeholder="Currency"
                value={createForm.currency}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, currency: e.target.value.toUpperCase() }))}
              />
              <input
                required
                type="date"
                value={createForm.effective_from}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, effective_from: e.target.value }))}
              />
              <input
                type="date"
                value={createForm.effective_to}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, effective_to: e.target.value }))}
              />

              <textarea
                className={styles.colSpan4}
                placeholder="Description"
                value={createForm.description}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, description: e.target.value }))}
              />

              <div className={styles.inlineActions}>
                <button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Create Procedure'}</button>
                <button type="button" className={styles.secondary} onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {editId ? (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>Edit Procedure</h2>
              <button type="button" className={styles.secondary} onClick={cancelEdit}>
                Close
              </button>
            </div>
            {error ? <div className={styles.error}>{error}</div> : null}
            <form onSubmit={handleUpdate} className={styles.grid4}>
              <input
                required
                placeholder="Procedure name"
                value={editForm.name}
                onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
              />
              <input
                placeholder="Category"
                value={editForm.category}
                onChange={(e) => setEditForm((prev) => ({ ...prev, category: e.target.value }))}
              />
              <input
                placeholder="Procedure type"
                value={editForm.procedure_type}
                onChange={(e) => setEditForm((prev) => ({ ...prev, procedure_type: e.target.value }))}
              />
              <input
                type="number"
                min={0}
                step="0.01"
                placeholder="Reimbursement amount"
                value={editForm.reimbursement_amount}
                onChange={(e) => setEditForm((prev) => ({ ...prev, reimbursement_amount: e.target.value }))}
              />

              <input
                type="date"
                value={editForm.effective_to}
                onChange={(e) => setEditForm((prev) => ({ ...prev, effective_to: e.target.value }))}
              />
              <select
                value={editForm.lifecycle_state}
                onChange={(e) => setEditForm((prev) => ({ ...prev, lifecycle_state: e.target.value }))}
              >
                <option value="active">active</option>
                <option value="suspended">suspended</option>
                <option value="retired">retired</option>
              </select>

              <textarea
                className={styles.colSpan4}
                placeholder="Description"
                value={editForm.description}
                onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
              />

              <div className={styles.inlineActions}>
                <button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
                <button type="button" onClick={cancelEdit} className={styles.secondary}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ProceduresPage;
